/**
 * The welcome card at the bottom of the screen, over the showcase planet: sign in or create an
 * account; once signed in, join a world with its code or create a new one.
 */
import { isGuest } from '@bramvasterd/terrella-protocol';
import { useState, type JSX } from 'react';

import { requestLobby } from '@game/session/lobby';
import { t } from '@i18n/index';
import { CreateForm, JoinForm } from '@ui/components/WorldForms';
import { useGame, useGameState, useLocale } from '@ui/hooks';
import { Logo } from '@ui/icons';

import './welcome.css';

type Step = 'pick' | 'join' | 'create';

export function Welcome(): JSX.Element {
  useLocale();
  const game = useGame();
  const me = useGameState((s) => s.me);
  const lobby = useGameState((s) => s.lobby);
  const [step, setStep] = useState<Step>('pick');
  const [hint, setHint] = useState<string | null>(null);
  const guest = me !== null && isGuest(me.id);
  const error = hint ?? (lobby.error ? t(`refused.${lobby.error}` as 'refused.network') : null);

  const back = (): void => {
    setStep('pick');
    setHint(null);
  };

  return (
    <div id="welcome">
      <div className="sheet welcome" role="dialog" aria-labelledby="welcome-title">
        {!me ? (
          <>
            <Logo className="mark" />
            <h2 id="welcome-title">{t('welcome.title')}</h2>
            <p className="lede">{t('welcome.lede')}</p>
            {error && (
              <p className="note warn" role="alert">
                {error}
              </p>
            )}
            <div className="stack">
              <button
                className="btn primary"
                type="button"
                disabled={lobby.busy}
                onClick={() => requestLobby(game, { kind: 'login' })}
              >
                {lobby.busy && <span className="spin" />}
                {t('welcome.login')}
              </button>
              <button
                className="btn"
                type="button"
                disabled={lobby.busy}
                onClick={() => requestLobby(game, { kind: 'register' })}
              >
                {t('welcome.register')}
              </button>
            </div>
          </>
        ) : step === 'pick' ? (
          <>
            <Logo className="mark" />
            <h2 id="welcome-title">{t('welcome.pick')}</h2>
            <p className="lede">{t('welcome.pick-text')}</p>
            {error && (
              <p className="note warn" role="alert">
                {error}
              </p>
            )}
            <div className="stack">
              <button
                className="btn primary"
                type="button"
                disabled={lobby.busy}
                onClick={() => setStep('join')}
              >
                {lobby.busy && <span className="spin" />}
                {t('welcome.join')}
              </button>
              {guest ? (
                <button
                  className="btn"
                  type="button"
                  disabled={lobby.busy}
                  onClick={() => requestLobby(game, { kind: 'register' })}
                >
                  {t('welcome.register')}
                </button>
              ) : (
                <button
                  className="btn"
                  type="button"
                  disabled={lobby.busy}
                  onClick={() => setStep('create')}
                >
                  {t('welcome.create')}
                </button>
              )}
            </div>
            <p className="note">
              {t(guest ? 'welcome.guest-as' : 'welcome.signed-in-as', { name: me.name })}
              {' · '}
              <button
                type="button"
                className="link"
                onClick={() => requestLobby(game, { kind: 'logout' })}
              >
                {t('welcome.logout')}
              </button>
            </p>
          </>
        ) : step === 'join' ? (
          <>
            <h2 id="welcome-title">{t('welcome.join')}</h2>
            <p className="lede">{t('welcome.join-text')}</p>
            <JoinForm onHint={setHint} />
            {error && (
              <p className="note warn" role="alert">
                {error}
              </p>
            )}
            <button type="button" className="btn quiet" onClick={back}>
              {t('welcome.back')}
            </button>
          </>
        ) : (
          <>
            <h2 id="welcome-title">{t('welcome.create')}</h2>
            <p className="lede">{t('welcome.create-text')}</p>
            <CreateForm onHint={setHint} />
            {error && (
              <p className="note warn" role="alert">
                {error}
              </p>
            )}
            <button type="button" className="btn quiet" onClick={back}>
              {t('welcome.back')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
